import { CalculatorIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useFetcher, useNavigate } from '@remix-run/react';
import {
	GQLDesignPageFragment,
	GQLMerchTileFragment,
	GQLOperationalSettingsFragment,
	GQLOrderItemFragment,
	GQLOrderItemPrintSideType,
	GQLOrderTileFragment,
	GQLProductFragment,
	GQLProductSideSideType,
	GQLProductTileFragment,
	GQLStoreFragment,
} from '@theprintbar/graphql';
import {
	ActionToastResponse,
	ActionToastType,
	CartType,
	DesignParamKeys,
	DesignParams,
	ImageSize,
	Merch,
	UpdateSource,
	WebRoutes,
	cn,
	getCartType,
	getCurrencyString,
	getInitialMerch,
	getMerchPrice,
	getMerchState,
	getMerchTurnaround,
	getSortedMerchDecorations,
	getSortedOrderItemDecorations,
	getSortedSides,
	getTurnaroundString,
	logError,
	mapEnumValue,
	merchInStock,
	merchQuantity,
	objectToFormData,
	updateMerchState,
} from '@theprintbar/shared';
import { useCallback, useEffect, useState } from 'react';
import {
	ActionToast,
	ButtonSize,
	CTAText,
	CalloutBar,
	Carousel,
	CartIcon,
	CustomColourIconHandler,
	FullCTATitleClass,
	Input,
	LightningBolt,
	MerchDesigner,
	Modal,
	OutlineButton,
	OutlineLinkButton,
	PriceBreakdown,
	PrimaryButton,
	ProductColours,
	ProductDetails,
	ProductImage,
	ProductSides,
	ProductSizeBubbles,
	ProductSizes,
	ProductTile,
	RichText,
	SamedayBar,
	StatusUpdateProps,
	SubmittingModal,
	WarningButton,
	gaCustomEvent,
	getFinalMerch,
	handleEditorUpdate,
	useWilcomStitches,
} from '../../components';
import type { CreativeEditorInstance } from '../../components/CreativeEditor';
import { TeamNamesSizes } from '../../components/DesignerElements/TeamNamesSizes';
import { useTutorial } from './components/Tutorial';
import { clearMerchSession, getMerchSession, saveMerchSession } from './utils/merchStateStorage';

interface DesignPageProps {
	product: GQLProductFragment;
	operationalSettings: GQLOperationalSettingsFragment;
	webDomain: string;
	assetsUrl: string;
	cesdkAssetsUrl: string;
	licence: string;
	designHandlerUrl: string;
	params: DesignParams;
	crew?: boolean;
	cartType?: CartType;
	isEdit?: boolean;
	content: GQLDesignPageFragment;
	referenceMerch?: GQLMerchTileFragment | null;
	orderItem?: GQLOrderItemFragment | null;
	order?: GQLOrderTileFragment;
	store?: GQLStoreFragment;
	className?: string;
	tutorialKey?: string;
	isQuote?: boolean;
	isAdditionalProduct?: boolean;
	customColourIconHandler?: CustomColourIconHandler;
	bestSellers?: GQLProductTileFragment[] | null;
	freeEdit?: boolean;
}

export function DesignPage({
	product,
	crew,
	referenceMerch,
	orderItem,
	order,
	operationalSettings,
	webDomain,
	assetsUrl,
	cesdkAssetsUrl,
	licence,
	designHandlerUrl,
	params,
	store,
	content,
	isEdit,
	className,
	tutorialKey,
	isQuote,
	isAdditionalProduct,
	bestSellers,
	freeEdit = true,
	customColourIconHandler,
}: DesignPageProps) {
	const [merch, setMerch] = useState<Merch>();
	const [updatingDesigner, setUpdatingDesigner] = useState(false);
	const [updatingPrice, setUpdatingPrice] = useState(false);
	const [editor, setEditor] = useState<CreativeEditorInstance>(null);
	const [activeSide, setActiveSide] = useState(
		(orderItem
			? mapEnumValue(GQLOrderItemPrintSideType, GQLProductSideSideType, getSortedOrderItemDecorations(orderItem)?.[0]?.side)
			: referenceMerch
			? getSortedMerchDecorations(referenceMerch)?.[0]?.side?.side
			: getSortedSides(product)[0]?.side) || getSortedSides(product)[0].side,
	);
	const [submittingStatus, setSubmittingStatus] = useState<StatusUpdateProps | undefined>();
	const [designerMobileOpen, setDesignerMobileOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [localtoast, setLocalToast] = useState<ActionToastResponse>();
	const { tutorialWindow, openTutorial } = useTutorial({ tutorialKey, designPage: content });
	const [warningPopup, setWarningPopup] = useState<CartType | null>(null);
	const [continueDesignPopup, setContinueDesignPopup] = useState<boolean>(false);
	const navigate = useNavigate();

	const { calculateStitches, resetWilcomCallId } = useWilcomStitches({ webDomain, setUpdatingPrice, setMerch, setLocalToast });

	const scrMinOrderQuantity = operationalSettings?.scrMinOrderQuantity || 25;

	const setMerchOnLoad = useCallback(async () => {
		if (!isEdit && params[DesignParamKeys.Action] !== 'duplicate' && !!(await getMerchSession())) {
			setContinueDesignPopup(true);
		} else {
			checkSameday();
			setMerch(
				getInitialMerch({
					product,
					params: {
						...params,
						sameday: order?.sameday || params.sameday,
					},
					referenceMerch,
					orderItem,
					store,
				}),
			);
		}
	}, [isEdit, params, product, referenceMerch, orderItem, store]);
	useEffect(() => {
		setMerchOnLoad();
	}, []);

	const checkSameday = useCallback(() => {
		const cartType = getCartType();
		//Lets warn the customer for some different mixed sameday scenarios
		if (cartType === CartType.Sameday && !product.isSameday) {
			setWarningPopup(CartType.Sameday);
			return false;
		} else if (cartType === CartType.Sameday && product.isSameday) {
			return true;
		}
	}, [product]);

	const handleContinueDesigning = useCallback(async (continueDesigning = false) => {
		if (continueDesigning) {
			setContinueDesignPopup(false);
			const existingSession = !!params[DesignParamKeys.Action] ? null : await getMerchSession();
			const initialMerch = existingSession || getInitialMerch({ product, params, referenceMerch, orderItem, store });
			const samedayOverride = checkSameday();

			if (samedayOverride !== undefined) {
				initialMerch.sameday = samedayOverride;
			}

			setMerch(initialMerch);
		} else {
			setContinueDesignPopup(false);
			await clearMerchSession();
			//refresh the page to clear any existing sessions
			window.location.reload();
		}
	}, []);

	const fetcher = useFetcher();

	const merchState = getMerchState({
		product,
		merch,
		updatingPrice,
		activeSide,
		store,
		crew,
		scrMinOrderQuantity,
	});

	useEffect(() => {
		if (fetcher.state === 'submitting') {
			setSubmittingStatus({
				type: 'submitting',
				title: 'Finalising merch...',
			});
		} else {
			setSubmittingStatus(undefined);
		}
	}, [fetcher.state]);

	const updateMerch = useCallback(
		async (updatedMerch: Merch, source: UpdateSource, firstUpdate?: boolean) => {
			const cartType = getCartType();
			if (source === 'design') {
				setIsLoading(false);
				resetWilcomCallId();
				setUpdatingPrice(false);
			}

			if (source === 'sameday' && cartType !== CartType.Empty) {
				const sMixed = cartType === CartType.Sameday && !updatedMerch.sameday;
				const nsMixed = cartType === CartType.Standard && updatedMerch.sameday;
				if (sMixed || nsMixed) {
					setWarningPopup(cartType);
				}
			}

			const newMerch = merch
				? updateMerchState({
						product,
						merch: updatedMerch,
						activeSide,
						isLoading,
						source,
						previousMerch: merch,
						operationalSettings,
				  })
				: updatedMerch;

			setMerch(newMerch);

			if (!isEdit && !firstUpdate) {
				await saveMerchSession(newMerch);
			}
		},
		[merch, activeSide, isEdit, isLoading, product, operationalSettings],
	);

	const handleCalculateStitches = useCallback(
		async (calculateAll = false) => {
			if (!merch) return;
			const newMerch = await handleEditorUpdate({
				editor,
				product,
				domain: webDomain,
				currentMerch: merch,
				dpi: 72,
			});

			calculateStitches(newMerch, calculateAll ? undefined : activeSide);
		},
		[merch, activeSide, editor, product, calculateStitches],
	);

	const submitMerch = useCallback(async () => {
		if (!merch) return;
		try {
			const newMerch = await getFinalMerch({
				editor,
				action: params[DesignParamKeys.Action],
				product,
				currentMerch: merch,
				webDomain,
				store,
				statusUpdateCallback: setSubmittingStatus,
			});

			if (!!store && !newMerch.sizing?.length) {
				newMerch.sizing = [
					{
						size: 'S',
						quantity: 1,
					},
				];
			}

			const merchObject = objectToFormData(newMerch);
			clearMerchSession();
			fetcher.submit(merchObject, {
				method: 'POST',
			});

			//Only GA event for web orders
			if (!isQuote && !store && !crew) {
				gaCustomEvent({
					action: 'add_to_cart',
					params: {
						currency: 'AUD',
						value: pricing.total.toFixed(2),
					},
					items: [
						{
							id: product,
							name: newMerch.name,
							quantity: newMerch.sizing.reduce((acc, curr) => acc + curr.quantity, 0),
							total: pricing.total,
						},
					],
				});
			}

			sessionStorage.setItem('cartOpened', '');

			setMerch({
				...merch,
			});
		} catch (e) {
			setSubmittingStatus(undefined);
			const message = await logError(e);
			setLocalToast({
				actionToast: {
					type: ActionToastType.Error,
					title: 'Something went wrong!',
					message,
				},
			});
		}
	}, [merch, editor, product, webDomain, fetcher, params, store, crew]);

	const handleSubmit = useCallback(async () => {
		if (!merch) return;
		const cartType = getCartType();
		if (cartType !== CartType.Empty) {
			const sdMixed = cartType === CartType.Sameday && !merch.sameday;
			const nsMixed = cartType === CartType.Standard && merch.sameday;
			//Lets warn the customer for some different mixed sameday scenarios
			if (sdMixed || nsMixed) {
				setWarningPopup(cartType);
				return;
			}
		}
		setSubmittingStatus({ type: 'generating', title: 'Creating merch...' });
		setTimeout(submitMerch, 1);
	}, [merch, fetcher, activeSide, editor, product, operationalSettings]);

	const pricing = getMerchPrice({
		merch,
		quantity: merchQuantity(merch),
		product,
		settings: operationalSettings,
		isSameday: !!merch?.sameday,
		store,
	});

	const turnaround = getMerchTurnaround({ settings: operationalSettings, merch, store, useStockedTurnaround: merchInStock({ product }) });

	return (
		<div className={cn('grid grid-cols-10 relative lg:gap-x-8 min-h-[70vh]', className)}>
			<h1 className="hidden">
				Design your {product.brand.name} {product.name}
			</h1>
			{tutorialWindow}
			<Modal open={!!continueDesignPopup} useLoading={false} className="pb-4">
				<div className="flex flex-col h-full">
					<div className="grid grid-cols-1 md:grid-cols-3 max-w-lg h-full">
						<img src="/images/loading.gif" className="mx-auto max-w-48 md:max-w-full w-full h-auto object-contain" />
						<div className="md:col-span-2 flex flex-col justify-between items-center mt-4">
							<RichText content={content.continueDesigning} />
							<div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center w-full mt-4">
								<OutlineButton className="w-full md:w-auto" onClick={() => handleContinueDesigning(true)}>
									Keep Designing
								</OutlineButton>
								<PrimaryButton className="mt-2 md:mt-0 w-full md:w-auto" onClick={() => handleContinueDesigning(false)}>
									Start Fresh
								</PrimaryButton>
							</div>
						</div>
					</div>
				</div>
			</Modal>
			<Modal open={!!warningPopup} useLoading={false} className="pb-4" onClose={() => setWarningPopup(null)}>
				<div className="flex flex-col h-full">
					<div className="grid grid-cols-3 max-w-lg h-full">
						<img src="/images/error.png" className="w-full h-auto object-contain" />
						<div className="col-span-2 flex flex-col items-center space-y-4 ">
							<RichText
								content={warningPopup === CartType.Sameday ? content.samedayCartWarning : content.nonSamedayCartWarning}
							/>
						</div>
					</div>
					<div className="flex justify-between items-center w-full mt-12">
						<OutlineLinkButton to={WebRoutes.Cart} onClick={() => setWarningPopup(null)}>
							View {isQuote ? 'Quote' : 'Cart'}
						</OutlineLinkButton>
						<PrimaryButton onClick={() => setWarningPopup(null)}>Close</PrimaryButton>
					</div>
				</div>
			</Modal>
			<ActionToast timeToClear={5000} localToast={localtoast} clearLocalToast={() => setLocalToast(undefined)} fetcher={fetcher} />
			<SubmittingModal statusUpdate={submittingStatus} />
			<div className="col-span-full lg:hidden lg:mb-4">
				<div className="flex mb-4">
					<ProductDetails product={product} store={store} />
				</div>
				{!store && product.isSameday && (!order || !!order.sameday) && (
					<SamedayBar
						settings={operationalSettings}
						product={product}
						merch={merch}
						disabled={!!order || !freeEdit}
						updateMerch={merch => updateMerch(merch, 'sameday')}
						greyedOut={!merchState.canSameday}
					/>
				)}
				<CalloutBar product={product} className="mt-4" />
			</div>
			<MerchDesigner
				isPod={!!store}
				crew={crew}
				editor={editor}
				content={content}
				merch={merch}
				orderItem={orderItem}
				merchItem={referenceMerch}
				domain={webDomain}
				assetsUrl={assetsUrl}
				cesdkAssetsUrl={cesdkAssetsUrl}
				licence={licence}
				product={product}
				activeSide={activeSide}
				designHandlerUrl={designHandlerUrl}
				updateMerch={(merch, firstUpdate) => updateMerch(merch, 'design', firstUpdate)}
				setEditor={setEditor}
				setUpdating={setUpdatingDesigner}
				onError={e => {
					setUpdatingDesigner(false);
					setLocalToast({ actionToast: { type: ActionToastType.Error, title: 'Error', message: e } });
				}}
				openTutorial={openTutorial}
				mobileOpen={designerMobileOpen}
				setMobileOpen={setDesignerMobileOpen}
			/>

			<div className="lg:col-span-5 col-span-full flex flex-col h-full space-y-2 lg:space-y-4">
				<div className="hidden lg:block">
					<div className="hidden lg:flex pb-2 mb-4">
						<ProductDetails product={product} store={store} />
					</div>
					{!store && product.isSameday && (!order || !!order.sameday) && (
						<SamedayBar
							settings={operationalSettings}
							product={product}
							merch={merch}
							disabled={!!order || !freeEdit}
							updateMerch={merch => updateMerch(merch, 'sameday')}
							// greyedOut={!merchState.canSameday}
						/>
					)}
					<CalloutBar product={product} className="mt-4" />
				</div>

				<ProductSides
					product={product}
					content={content}
					operationalSettings={operationalSettings}
					editor={editor}
					merch={merch}
					crew={crew}
					freeEdit
					webDomain={webDomain}
					calculateStitches={handleCalculateStitches}
					updatingPrice={updatingPrice}
					updatingDesigner={isLoading || updatingDesigner}
					activeSide={activeSide}
					setActiveSide={setActiveSide}
					updateMerch={merch => updateMerch(merch, 'decoration')}
					setMobileOpen={setDesignerMobileOpen}
					onRefresh={clearMerchSession}
					store={store}
				/>
				{!!store && !crew && (
					<Input
						value={merch?.name}
						onChange={e => {
							if (!merch) return;
							setMerch({ ...merch, name: e.currentTarget.value });
						}}
						outsideForm
						placeholder="Give your merch a name"
						name="name"
						label="Name"
					/>
				)}
				<ProductColours
					loading={isLoading}
					freeEdit={freeEdit}
					available={!!store && !crew}
					product={product}
					merch={merch}
					store={store}
					customColourIconHandler={customColourIconHandler}
					updateMerch={merch => updateMerch(merch, 'colour')}
				/>
				{!!store && !crew ? (
					<div className="space-y-2.5">
						<h5 className="uppercase text-xs whitespace-nowrap">Available sizes</h5>
						<div className={cn(isLoading && 'opacity-50 pointer-events-none')}>
							<ProductSizeBubbles product={product} colourCode={merch?.colour} />
						</div>
					</div>
				) : !merch?.teamNames?.available.length ? (
					<ProductSizes
						loading={isLoading || updatingDesigner}
						product={product}
						freeEdit={freeEdit}
						merch={merch}
						updateMerch={merch => updateMerch(merch, 'quantity')}
						scrMinOrderQuantity={scrMinOrderQuantity}
					/>
				) : (
					<TeamNamesSizes
						product={product}
						merch={merch}
						editor={editor}
						updateMerch={merch => updateMerch(merch, 'teamNames')}
						teamNameOptions={content.teamNameOptions?.structure ?? undefined}
					/>
				)}
				<div className="col-span-full lg:col-span-5">
					{turnaround > 0 && !store && !crew && (
						<div className="flex items-center justify-between mb-2">
							<h5 className="text-xs uppercase inline">Expected Turnaround:</h5>{' '}
							<span className="text-xs flex items-center">
								{turnaround === 1 && <LightningBolt className="inline w-4 h-4 mr-0.5" />}
								{getTurnaroundString(turnaround)}
							</span>
						</div>
					)}
					<PriceBreakdown className="mt-0" pricing={pricing} disabled={!merchState.canSubmit} />
				</div>
				<div className="flex flex-col flex-grow justify-end">
					{!!store && !crew && (
						<div className="mb-8">
							<h5 className="text-xs uppercase">Turnaround</h5>
							<p className="text-sm">
								{!merchState.canSubmit
									? `${merchState.title} to see turnaround`
									: `${getMerchTurnaround({
											settings: operationalSettings,
											merch,
											store,
											useStockedTurnaround: merchInStock({
												product,
											}),
									  })} Business Days`}
							</p>
						</div>
					)}
					{!!merchState.quantity && merchState.hasEmbWithoutStitches ? (
						<WarningButton
							size={ButtonSize.L}
							className="w-full"
							disabled={merchState.hasErrors}
							onClick={() => handleCalculateStitches(true)}
							icon={CalculatorIcon}
						>
							{updatingPrice ? 'Calculating...' : 'Calculate Pricing'}
						</WarningButton>
					) : (
						<PrimaryButton
							onClick={handleSubmit}
							size={ButtonSize.L}
							disabled={!merchState.canSubmit || !!submittingStatus}
							icon={!!store ? PlusIcon : CartIcon}
							className="w-full font-display lg:text-xl text-lg"
						>
							{isLoading ? (
								'Loading...'
							) : !!submittingStatus ? (
								'Creating Merch...'
							) : !!merch && merchState.canSubmit ? (
								<>
									{pricing.subtotal !== pricing.total && (
										<span className="italic line-through mr-2 opacity-80">{getCurrencyString(pricing.subtotal)}</span>
									)}
									{getCurrencyString(pricing.total)} •{' '}
									{isEdit || (params.action === 'edit' && !!params.ref) ? 'Update' : !!crew || !store ? 'Add to' : 'Add'}{' '}
									{crew ? 'Order' : !!store ? 'Merch' : isQuote ? 'Quote' : 'Cart'}
								</>
							) : (
								merchState.title
							)}
						</PrimaryButton>
					)}
					{merchState.canSubmit && pricing.subtotal !== pricing.total && (
						<p className="text-end text-xs text-grey mt-1">*Volume discount applied</p>
					)}
				</div>
			</div>
			{!!product.info?.document && (
				<div className="col-span-full my-12 grid lg:grid-cols-2 gap-8 border-t border-blue pt-12" id="info">
					<h2 className={cn(FullCTATitleClass('Product Info'), 'text-3xl lg:text-[40px] col-span-full')}>Product Info</h2>
					<div>
						<div className="lg:hidden px-8 mb-4">
							<ProductImage product={product} webDomain={webDomain} size={ImageSize.ProductFull} />
						</div>
						<CTAText text={{ content: product.info }} className="text-base 2xl:text-lg" />
					</div>

					<div className="justify-center lg:justify-end hidden lg:flex">
						<div className="w-full max-w-xl">
							<ProductImage product={product} size={ImageSize.ProductFull} webDomain={webDomain} />
						</div>
					</div>
				</div>
			)}
			{bestSellers && bestSellers.length > 0 && (
				<div className="col-span-full">
					<h4 className="font-display font-light text-2xl lg:text-3xl break-words normal-case mb-4 lg:mb-0 lg:max-w-lg pb-12">
						You might also like
					</h4>
					<Carousel
						carouselKey="bestseller-carousel"
						slidesPerView="auto"
						className="px-2 lg:px-4"
						slides={
							bestSellers?.map(product => ({
								containerProps: {
									className: 'px-2 lg:px-4 max-w-56 lg:max-w-80',
								},
								content: (
									<div className="h-full w-full relative mt-4">
										<ProductTile
											params={undefined}
											product={product}
											productPath={WebRoutes.Product(product.slug)}
											calculator={WebRoutes.Calculator(product.code)}
											webDomain={webDomain}
											reloadDocument
										/>
									</div>
								),
							})) || []
						}
					/>
				</div>
			)}
		</div>
	);
}
